import {optimisticCacheProduct} from '@/cart/optimistic-updates/optimistic-cache-product';
import {getProductSelection, setProduct} from '@/product/product-selection';
import {productSchema} from '@/product/types';
import {setProductSellingPlans} from '@/selling-plan/selling-plan-store';
import {logError, logInfo} from '@/utilities/log';
import {Plan} from '@rechargeapps/storefront-client';
import {ZodError} from 'zod';
import createOptimisticSellingPlan from '../create-optimistic-selling-plan';

/**
 * Loads the product selection from the provided JSON string.
 * If the product selection is not already loaded, it parses the JSON string,
 * creates a product object, and sets the product selection with the product
 * and the selected variant.
 *
 * @param productJson - The JSON string representing the product.
 * @throws If the product data fails to parse.
 */
export function loadProductJson(productJson: string) {
  const {product} = getProductSelection();
  if (product) return;

  try {
    const product = productSchema.parse(JSON.parse(productJson));

    const plans: Plan[] = [];

    for (const group of product.sellingPlanGroups) {
      const plan = createOptimisticSellingPlan(group, product);
      if (plan) {
        plans.push(plan);
      }
    }

    setProductSellingPlans(product.id, plans);
    setProduct(product);

    // Cache the product data so we can display faster in the cart
    optimisticCacheProduct(product);

    logInfo('ProductSelectionLoaded', {productJson, product});
  } catch (error) {
    logError(error, {
      message: 'ErrorLoadingProductSelection',
      productJson,
      errors: (error as ZodError).errors,
    });
    throw error;
  }
}
